/**
 * Invite-only membership, sharing and influence (GH-864, DEC-051 with the owner's amendments of
 * 2026-10-03). The wire shapes every slice builds against: GH-864b (membership API), GH-864c
 * (share and influence API), GH-864d (iOS), GH-864e (donor web), GH-864f (console).
 *
 *   GET  /v1/public/membership/pulse                          none     membershipPulseResponse
 *   GET  /v1/public/invites/:code                             none     publicInviteResponse
 *   GET  /v1/public/events/:eventId/member-invite-token       none     eventMemberInviteTokenResponse
 *   GET  /v1/donor/membership                                 donor    donorMembershipResponse
 *   POST /v1/donor/waitlist                                   donor    {} → donorMembershipResponse
 *   POST /v1/donor/invites/redeem                             donor    redeemInviteRequest → donorMembershipResponse
 *   POST /v1/donor/membership/event-join                      donor    eventMembershipJoinRequest → eventMembershipJoinResponse
 *   POST /v1/donor/invites                                    member   (Idempotency-Key) → issuedInviteResponse
 *   POST /v1/donor/invites/:inviteId/reissue                  member   {} → issuedInviteResponse
 *   POST /v1/donor/invites/:inviteId/revoke                   member   {} → memberInvite
 *   PUT  /v1/donor/membership/preferences                     member   membershipPreferencesRequest → donorMembershipResponse
 *   GET  /v1/donor/share-link?organizationId=|eventId=        donor?   shareLinkResponse
 *   POST /v1/donor/share-code/rotate                          member   {} → { rotated: true }
 *   POST /v1/donor/shares                                     member   recordShareRequest → recordShareResponse
 *   GET  /v1/donor/influence                                  member   influenceResponse
 *   PUT  /v1/organizations/:organizationId/member-sharing     staff    memberSharingRequest → memberSharingResponse
 *   GET  /v1/organizations/:organizationId/home (extended)    staff    sources.memberShares: memberShares
 *   GET  /v1/admin/membership                                 platform adminMembershipSummary
 *   POST /v1/admin/membership/waves                           platform createWaveRequest → admissionWave (step-up)
 *   POST /v1/admin/members/:memberId/invites-freeze           platform freezeInvitesRequest → { frozen }
 *   GET  /v1/admin/members/lookup?number=N                    platform adminMemberLookupResponse (step-up, audited)
 *   POST /v1/jobs/project-influence                           operator projectInfluenceRequest → projectInfluenceResponse
 *   POST /v1/jobs/send-member-digests                         operator {} → sendMemberDigestsResponse
 *
 * Every new route answers `cache-control: no-store`. `member` routes answer `membership_required`
 * (403) to a signed-in non-member; giving, quotes, receipts, saved cards and staff routes never
 * consult membership.
 *
 * ## Privacy, in the shapes themselves
 *
 * Influence is **points, never money**: a display string of digits and commas, no `$`, no decimal.
 * Each figure is `display | null` with a `withheld` reason; the API withholds below 5 distinct
 * givers (k = 5, DEC-049's threshold), reveals once a week (Monday 00:00 UTC) and rounds. Strict
 * objects: no giver, organization, event or amount field can be added to an influence response
 * without breaking this contract on purpose.
 */
import { z } from "zod";
import { homeMemberSharesSchema } from "./v1.js";
/* ------------------------------------------------------------------------------- rules */
/**
 * The owner's numbers (amendments 2026-10-03). Grants are append-only rows in
 * `member_invite_grants`; these are the quantities GH-864b writes. **There is no cap.**
 */
export const MEMBERSHIP_RULES = {
    /** Invites a new (or founding) member starts with — `reason = 'join' | 'founding'`. */
    startingInvites: 5,
    /** Each time one of your invitees joins — `reason = 'invitee_joined'`. */
    inviteeJoinedGrant: 1,
    /** Each posted online gift by a direct invitee — `reason = 'invitee_donated'`, never clawed back. */
    inviteeDonatedGrant: 3,
    /** An invite is single-use and lapses after this many days (its slot then comes back). */
    inviteExpiryDays: 30,
    /** Member numbers up to this are "Founding member"; so is every `joined_via = 'founding'`. */
    foundingMemberNumberLimit: 1000,
    /** The event big-screen QR token's lifetime; the display re-mints at half of it. */
    eventInviteTokenTtlSeconds: 600,
    /** k: fewer distinct givers than this and a figure is withheld. */
    privacyMinimumGivers: 5,
    /** Points count toward levels once this old (reversals still post after). */
    levelMaturityDays: 14,
    /** "People waiting" shows only from this many, floored to the hundred. */
    waitingDisplayMinimum: 100,
    /** How long a device keeps a `ref` before the gift (credit itself never expires). */
    shareRefRetentionDays: 7,
    /** The invite-chain walk stops here — a safety bound against a bug, not a product rule. */
    influenceChainSafetyBound: 64,
};
/* ------------------------------------------------------------------------------- codes */
/** Crockford base32: the alphabet every invite and share code is drawn from (GH-864h #26). */
export const INVITE_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const INVITE_CODE_LENGTH = 10;
/** A normalized code: ten symbols of the alphabet, 50 bits. */
export const INVITE_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{10}$/;
const CROCKFORD = INVITE_CODE_PATTERN;
/**
 * An invite or share code as a person typed or pasted it: upper-cased, spaces and hyphens
 * dropped, and Crockford's look-alikes folded (I and L → 1, O → 0). Ten symbols, 50 bits.
 */
export function normalizeInviteCode(raw) {
    return raw.toUpperCase().replace(/[\s-]/g, "").replace(/[IL]/g, "1").replace(/O/g, "0");
}
export const inviteCodeSchema = z
    .string()
    .max(32)
    .transform(normalizeInviteCode)
    .pipe(z.string().regex(CROCKFORD, "not an invite code"));
/** A member's share code as it rides on a link (`?ref=`). Same alphabet and length. */
export const shareRefSchema = inviteCodeSchema;
/** The query param the display's QR adds to `/c/<eventId>` for the auto-invite. */
export const EVENT_INVITE_QUERY_PARAM = "join";
/**
 * A signed token (`packages/signing/src/hmac.ts`: base64url body "." base64url signature).
 * Purpose `event_member_invite`, signed with the access-key family, carrying the event id and an
 * expiry of `eventInviteTokenTtlSeconds`. Stateless: nothing stores it.
 */
export const eventInviteTokenSchema = z
    .string()
    .max(1024)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "not a signed token");
/* ----------------------------------------------------------------------------- points */
/** About one point per dollar: whole dollars of gross, never negative. */
export function influencePointsFromMinor(netMinor) {
    if (netMinor <= 0n)
        return 0;
    return Number(netMinor / 100n);
}
/** Rounded down: to 10 below 1,000, to 50 below 10,000, to 100 above. */
export function roundInfluencePoints(points) {
    const whole = Math.max(0, Math.floor(points));
    const step = whole < 1_000 ? 10 : whole < 10_000 ? 50 : 100;
    return Math.floor(whole / step) * step;
}
/** What a donor reads: rounded, grouped digits. Never a `$`, never a decimal. */
export function displayInfluencePoints(points) {
    return roundInfluencePoints(points).toLocaleString("en-US");
}
/** Denomination-neutral level names, from net matured points. Derived, never stored. */
export const INFLUENCE_LEVELS = [
    { index: 0, name: "Neighbor", at: 0 },
    { index: 1, name: "Connector", at: 100 },
    { index: 2, name: "Gatherer", at: 500 },
    { index: 3, name: "Cornerstone", at: 2_500 },
    { index: 4, name: "Keystone", at: 10_000 },
];
export function influenceLevelFor(maturedPoints) {
    let current = INFLUENCE_LEVELS[0];
    for (const level of INFLUENCE_LEVELS)
        if (maturedPoints >= level.at)
            current = level;
    const next = INFLUENCE_LEVELS[current.index + 1];
    return { name: current.name, index: current.index, nextAt: next ? next.at : null };
}
/* ------------------------------------------------------------------------- primitives */
const uuid = z.string().uuid();
const timestamp = z.string().datetime({ offset: true });
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** "1,240" — digits and commas, so a figure can never be styled as money. */
const pointsDisplay = z.string().regex(/^\d{1,3}(,\d{3})*$/);
const count = z.number().int().nonnegative();
export const SHARE_KINDS = ["invite", "organization", "event", "receipt", "progress"];
/** GH-884: `ticket` — joined by the app invite a ticket came with (migration 0118). */
export const MEMBER_JOINED_VIA = [
    "invite",
    "event",
    "wave",
    "founding",
    "staff",
    "ticket",
];
export const INVITE_STATUSES = ["open", "redeemed", "revoked", "expired"];
export const MILESTONE_IDS = [
    "founding_member",
    "first_invite_redeemed",
    "five_givers",
    "level_connector",
    "level_gatherer",
    "level_cornerstone",
    "level_keystone",
    "streak_4_weeks",
    "streak_12_weeks",
    "streak_52_weeks",
];
/** A prewritten share-sheet message: the platform's neutral default or the organization's own. */
export const shareMessageSchema = z
    .object({ id: z.string().regex(/^[a-z0-9_]{1,40}$/), text: z.string().min(1).max(400) })
    .strict();
/* --------------------------------------------------------------------- public routes */
export const membershipPulseResponseSchema = z
    .object({
    /** "Over 1,200 waiting", or null below `waitingDisplayMinimum`. Never inflated. */
    waitingDisplay: z.string().nullable(),
})
    .strict();
/** One shape for unknown, used, revoked and expired: `unavailable`, with nothing else. */
export const publicInviteResponseSchema = z
    .object({
    status: z.enum(["available", "unavailable"]),
    /** "Amina R." — null when unavailable or the inviter turned their name off. */
    inviterName: z.string().max(80).nullable(),
    expiresOn: isoDate.nullable(),
})
    .strict();
/**
 * `POST /v1/public/invites/lookup` (GH-864h SEC-M3): the code travels in the body, never in a URL,
 * so request logs never carry it. Any string up to 64 characters is accepted and answered with the
 * same uniform shape; a malformed code is just an unavailable one.
 */
export const publicInviteLookupRequestSchema = z.object({ code: z.string().max(64) }).strict();
/**
 * For the display's QR. While the event is not open (live, or published and inside its window)
 * the token, url and expiry are null and the display shows its plain check-in code. The display
 * polls again after `refreshAfterSeconds`.
 */
export const eventMemberInviteTokenResponseSchema = z
    .object({
    token: eventInviteTokenSchema.nullable(),
    /** `/c/<eventId>?via=display_qr&join=<token>` on the public web origin. */
    url: z.string().url().nullable(),
    expiresAt: timestamp.nullable(),
    refreshAfterSeconds: z.number().int().min(10).max(3600),
})
    .strict();
/* -------------------------------------------------------------------- donor membership */
export const memberInviteSchema = z
    .object({
    id: uuid,
    createdAt: timestamp,
    expiresAt: timestamp,
    status: z.enum(INVITE_STATUSES),
})
    .strict();
export const membershipMemberSchema = z
    .object({
    number: z.number().int().positive(),
    founding: z.boolean(),
    joinedAt: timestamp,
    joinedVia: z.enum(MEMBER_JOINED_VIA),
    status: z.enum(["active", "suspended"]),
    invitesFrozen: z.boolean(),
    showNameOnInvites: z.boolean(),
    countForInviter: z.boolean(),
    /** The opt-in email digest — a consent record, purpose `member_weekly_digest`. */
    weeklyDigest: z.boolean(),
})
    .strict();
export const donorMembershipResponseSchema = z
    .object({
    state: z.enum(["member", "waiting", "none"]),
    member: membershipMemberSchema.nullable(),
    waitlist: z
        .object({
        /** 1-based, derived from `waitlist_queue`. */
        position: z.number().int().positive(),
        waitingDisplay: z.string().nullable(),
    })
        .strict()
        .nullable(),
    invites: z
        .object({
        /** Granted minus held. No cap: any non-negative number. */
        available: count,
        items: z.array(memberInviteSchema).max(500),
        /**
         * GH-871a (DEC-052): present, and true, only for a platform admin's account, which issues
         * without an allotment — the client shows no count. Absent for everyone else.
         */
        unlimited: z.literal(true).optional(),
    })
        .strict()
        .nullable(),
})
    .strict()
    .refine((value) => (value.state === "member") === (value.member !== null && value.invites !== null) &&
    (value.state === "waiting") === (value.waitlist !== null), { message: "state must match the parts present" });
export const joinWaitlistRequestSchema = z.object({}).strict();
/** Errors: `invite_unavailable` (uniform for unknown, used, revoked, expired), `already_member`. */
export const redeemInviteRequestSchema = z.object({ code: inviteCodeSchema }).strict();
export const eventMembershipJoinRequestSchema = z
    .object({ token: eventInviteTokenSchema })
    .strict();
/**
 * `joined`: a member now (`joined_via = 'event'`). `already_member`: nothing changed.
 * `waitlisted`: the token had expired or the event had closed — the donor is on the waitlist
 * and the client says "Ask a member for an invite". Never an error for an expired token.
 */
export const eventMembershipJoinResponseSchema = z
    .object({
    outcome: z.enum(["joined", "already_member", "waitlisted"]),
    membership: donorMembershipResponseSchema,
})
    .strict();
/** The code is shown exactly once, here; only its HMAC is stored. */
export const issuedInviteResponseSchema = z
    .object({
    inviteId: uuid,
    code: z.string().regex(CROCKFORD),
    /** `/i/<code>` on the public web origin. */
    url: z.string().url(),
    expiresAt: timestamp,
    messages: z.array(shareMessageSchema).min(1).max(3),
})
    .strict();
export const membershipPreferencesRequestSchema = z
    .object({
    showNameOnInvites: z.boolean().optional(),
    countForInviter: z.boolean().optional(),
    weeklyDigest: z.boolean().optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: "change at least one preference",
});
/* -------------------------------------------------------------------------- sharing */
export const shareLinkQuerySchema = z
    .object({ organizationId: uuid.optional(), eventId: uuid.optional() })
    .strict()
    .refine((value) => (value.organizationId === undefined) !== (value.eventId === undefined), {
    message: "exactly one of organizationId or eventId",
});
/** Members get a `?ref=` link that earns credit; everyone else a plain link. Never gated. */
export const shareLinkResponseSchema = z
    .object({
    url: z.string().url(),
    credited: z.boolean(),
    messages: z.array(shareMessageSchema).min(1).max(3),
})
    .strict();
export const rotateShareCodeResponseSchema = z.object({ rotated: z.literal(true) }).strict();
/** Client-reported and cosmetic only (the streak); rate limited, once per ISO week counts. */
export const recordShareRequestSchema = z.object({ kind: z.enum(SHARE_KINDS) }).strict();
export const recordShareResponseSchema = z.object({ streakWeeks: count }).strict();
/* ------------------------------------------------------------------------- influence */
/**
 * One figure under the privacy rule. `display` is lifetime points as of `asOf`; `thisWeek` is the
 * week that ended at `asOf`, shown only if that week alone had k distinct givers (otherwise it
 * rolls silently into the lifetime figure). `withheld` says why `display` is null.
 */
export const influenceFigureSchema = z
    .object({
    display: pointsDisplay.nullable(),
    thisWeek: pointsDisplay.nullable(),
    withheld: z.enum(["fewer_than_5_givers"]).nullable(),
})
    .strict()
    .refine((value) => (value.display === null) === (value.withheld !== null), {
    message: "a figure is shown or withheld, never both",
})
    .refine((value) => value.display !== null || value.thisWeek === null, {
    message: "no weekly figure while the lifetime figure is withheld",
});
export const influenceResponseSchema = z
    .object({
    /** The Monday (UTC) the figures are frozen at. */
    asOf: isoDate,
    level: z
        .object({
        name: z.string().min(1).max(40),
        index: z.number().int().min(0),
        nextAt: z.number().int().positive().nullable(),
    })
        .strict(),
    /** Everything credited to you, at every depth. */
    points: influenceFigureSchema,
    /** Depth 1: your own share links and the people you invited. */
    direct: influenceFigureSchema,
    /** Depth 2 and beyond: your invitees' invitees, and theirs. */
    network: influenceFigureSchema,
    /** Consecutive ISO weeks with a share; shown only while active. Never a "lost" message. */
    streakWeeks: count,
    milestones: z
        .array(z.object({ id: z.enum(MILESTONE_IDS), reachedOn: isoDate }).strict())
        .max(MILESTONE_IDS.length),
})
    .strict();
/* -------------------------------------------------------------------- organizations */
/**
 * An organization's share message goes into strangers' message threads, so it may carry nothing
 * invisible (GH-864h SEC-L5): no C0/C1 control characters (line breaks included — it is one line),
 * and no bidirectional embedding, override or isolate marks (U+202A–202E, U+2066–2069, U+200E/F,
 * U+061C), which can make a link read as something it is not. Letters of every script stay; a
 * right-to-left message renders by the Unicode algorithm without them. Spaces are collapsed.
 */
// eslint-disable-next-line no-control-regex -- the control characters are what it removes
const INVISIBLE_IN_SHARE_MESSAGE = /[\u0000-\u001F\u007F-\u009F؜‎‏‪-‮⁦-⁩]/g;
export function sanitizeShareMessage(text) {
    return text.replace(INVISIBLE_IN_SHARE_MESSAGE, " ").replace(/\s+/g, " ").trim();
}
export const memberSharingRequestSchema = z
    .object({
    enabled: z.boolean(),
    shareMessage: z
        .string()
        .transform(sanitizeShareMessage)
        .pipe(z.string().min(1).max(140))
        .nullable(),
})
    .strict();
export const memberSharingResponseSchema = memberSharingRequestSchema;
/** The Home sources line: gifts that came through members' links. Withheld below k = 5 gifts. */
export const memberSharesSchema = homeMemberSharesSchema;
/* ---------------------------------------------------------------------- platform staff */
export const adminMembershipSummarySchema = z
    .object({
    members: count,
    waiting: count,
    /** Every invite ever issued, reissues included (GH-864g). */
    invitesIssued: count,
    /** Invites someone joined with (GH-864g). */
    invitesRedeemed: count,
    invitesOutstanding: count,
    invitesFrozenMembers: count,
    suspendedMembers: count,
    lastWaveAt: timestamp.nullable(),
    /**
     * GH-871d: admitted account-less people whose code was not confirmed sent and will not be
     * retried by itself (unconfirmed, not delivered, or failed past its last attempt).
     */
    admissionsUntold: count,
})
    .strict();
export const createWaveRequestSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("admission"),
        /** How many to admit from the head of the waitlist, FIFO. */
        count: z.number().int().min(1).max(10_000),
        reason: z.string().trim().min(1).max(500),
    })
        .strict(),
    z
        .object({
        kind: z.literal("bonus"),
        /** Extra invites for every active member. */
        invitesPerMember: z.number().int().min(1).max(50),
        reason: z.string().trim().min(1).max(500),
    })
        .strict(),
]);
export const admissionWaveSchema = z
    .object({
    id: uuid,
    kind: z.enum(["admission", "bonus"]),
    admittedCount: count,
    invitesPerMember: count,
    createdAt: timestamp,
})
    .strict();
export const freezeInvitesRequestSchema = z
    .object({ frozen: z.boolean(), reason: z.string().trim().min(1).max(500) })
    .strict();
/**
 * `POST /v1/admin/members/:memberId/suspension` (GH-864h SEC-M1): suspend or reinstate one member.
 * Platform staff, step-up, audited. A suspended member keeps giving, receipts and saved cards
 * (membership never gates them); they earn nothing, are credited nothing, and their invites read
 * as unavailable.
 */
export const suspendMemberRequestSchema = z
    .object({ suspended: z.boolean(), reason: z.string().trim().min(1).max(500) })
    .strict();
export const suspendMemberResponseSchema = z.object({ suspended: z.boolean() }).strict();
/**
 * `GET`/`PUT /v1/admin/membership/settings` (GH-864h): the two levers platform staff may move.
 * The per-event join ceiling bounds one event QR's admissions (threat model T3). The minimum gift
 * (minor units, as a string) is the smallest gift that counts toward the five givers and earns the
 * +3 `invitee_donated` grant (T2); 0 keeps the owner's "+3 for every donation".
 */
export const membershipSettingsSchema = z
    .object({
    eventJoinCeiling: z.number().int().min(0).max(100_000),
    minimumGiftMinor: z.string().regex(/^\d{1,9}$/),
})
    .strict();
export const updateMembershipSettingsRequestSchema = membershipSettingsSchema
    .extend({ reason: z.string().trim().min(1).max(500) })
    .strict()
    .refine((value) => BigInt(value.minimumGiftMinor) <= 1000000n, {
    message: "The minimum gift is at most $10,000.",
    path: ["minimumGiftMinor"],
});
/**
 * `GET /v1/admin/members/lookup?number=214` (GH-864g): find one member to act on, by member number
 * only. "#214", "214" and "1,214" are accepted. There is no lookup by email or phone: no staff
 * surface looks a donor up by contact today, and this one does not start.
 */
export const adminMemberLookupQuerySchema = z
    .object({
    number: z
        .string()
        .max(20)
        .transform((raw) => raw.trim().replace(/^#/, "").replace(/,/g, ""))
        .pipe(z.string().regex(/^[1-9]\d{0,11}$/, "not a member number"))
        .transform(Number),
})
    .strict();
/**
 * The minimum a platform staff member needs to act: the id the freeze route takes, the number
 * they typed, a short name to confirm it is the right person ("Amina R."), and the two switches.
 * No contact, no giving, no influence.
 */
export const adminMemberLookupResponseSchema = z
    .object({
    memberId: uuid,
    memberNumber: z.number().int().positive(),
    displayName: z.string().max(80).nullable(),
    status: z.enum(["active", "suspended"]),
    invitesFrozen: z.boolean(),
})
    .strict();
/* ----------------------------------------------------------------------------- jobs */
export const projectInfluenceRequestSchema = z
    .object({ limit: z.number().int().min(1).max(500).optional() })
    .strict();
export const projectInfluenceResponseSchema = z
    .object({ attributed: count, projected: count })
    .strict();
export const sendMemberDigestsResponseSchema = z.object({ sent: count }).strict();
//# sourceMappingURL=membership.js.map